/**
 * `marketing_params` block. Captured once at `Contextune.init()` and reused —
 * even if the URL changes mid-session (SPA route change), the snapshot reports
 * the landing-page params.
 *
 * Every string field here lands in an LLM system prompt downstream, so
 * each is passed through:
 *   - `sanitiseForLLM` (strip prompt-control markers, control chars, length-cap)
 *   - `redactUrl` for URL-shaped fields (`landing_page`, `referrer`)
 *
 * See `.agents/security-review.md` F1 (prompt injection) and F2 (URL PII).
 */

import type { MarketingParamsBlock } from '../types.js';
import { sanitiseForLLM, SANITISE_URL_LIMIT } from '../sanitise/forLLM.js';
import { redactUrl } from '../sanitise/url.js';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/** UTM values are short by spec; tighter cap reduces prompt-budget noise. */
const UTM_LIMIT = 128;

export function captureMarketingParams(): MarketingParamsBlock {
  const loc = typeof window !== 'undefined' ? window.location : undefined;
  const doc = typeof document !== 'undefined' ? document : undefined;

  const search = loc?.search ?? '';
  const params = new URLSearchParams(search);

  const rawReferrer = doc?.referrer && doc.referrer.length > 0 ? doc.referrer : null;
  const rawLandingPage = loc?.href ?? null;

  const block: MarketingParamsBlock = {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    referrer:
      rawReferrer === null
        ? null
        : sanitiseForLLM(redactUrl(rawReferrer, SANITISE_URL_LIMIT), SANITISE_URL_LIMIT),
    landing_page:
      rawLandingPage === null
        ? null
        : sanitiseForLLM(
            redactUrl(rawLandingPage, SANITISE_URL_LIMIT),
            SANITISE_URL_LIMIT
          ),
  };

  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v !== null) block[key] = sanitiseForLLM(v, UTM_LIMIT);
  }

  return block;
}
